<template>
  <!-- 项目信息：迭代批次(卡片) / 任务 / 交付文件 / 项目日志 / 项目人员与测试 -->
  <div class="project-info">
    <div class="info-body">
      <!-- 迭代批次（卡片） -->
      <div class="block-title">迭代批次</div>
      <div v-if="!sprintList.length" class="empty-block">尚未排入迭代批次</div>
      <div v-else class="sprint-cards">
        <div v-for="s in sprintList" :key="s.id" class="sprint-card">
          <div class="sc-head">
            <span class="sc-code">{{ s.sprintCode || '—' }}</span>
            <el-tag size="small" :type="sprintStateTag(s.state)">{{ s.state || '—' }}</el-tag>
          </div>
          <div class="sc-name">{{ s.sprintName || '—' }}</div>
          <div v-if="s.goal" class="sc-goal ellipsis">{{ s.goal }}</div>
          <div class="sc-meta">
            <span>经理：{{ s.managerName || '—' }}</span>
            <span>期望：{{ dateOnly(s.expectDate) }}</span>
            <span>任务 {{ s.taskCount || 0 }}</span>
          </div>
          <div class="sc-progress">
            <el-progress :percentage="s.progress || 0" :stroke-width="8" :show-text="false"/>
            <span class="progress-text">{{ s.progress || 0 }}%</span>
          </div>
        </div>
      </div>

      <!-- 任务 -->
      <div class="block-title">任务（{{ taskList.length }}）</div>
      <el-table :data="taskList" border size="small" :empty-text="'尚未拆解任务'">
        <el-table-column label="任务" min-width="200">
          <template v-slot="{row}">
            <b>{{ row.taskName || '未命名任务' }}</b>
            <div v-if="row.taskCode" class="muted code-text">{{ row.taskCode }}</div>
          </template>
        </el-table-column>
        <el-table-column label="负责人" prop="ownerName" width="90">
          <template v-slot="{row}">{{ row.ownerName || '—' }}</template>
        </el-table-column>
        <el-table-column label="计划" width="200">
          <template v-slot="{row}">{{ dateOnly(row.planStart) }} ~ {{ dateOnly(row.planEnd) }}</template>
        </el-table-column>
        <el-table-column label="进度" width="150">
          <template v-slot="{row}">
            <el-progress :percentage="row.progress || 0" :stroke-width="6" :show-text="false" style="width: 110px; display: inline-block"/>
            <span class="progress-text">{{ row.progress || 0 }}%</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template v-slot="{row}">
            <el-tag size="small" :type="taskStatusTag(row.taskStatus)">{{ taskStatusLabel(row.taskStatus) || '—' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="延期" width="110" align="center">
          <template v-slot="{row}">
            <span v-if="row.delayState && row.delayState !== '正常'" class="delay-text">
              {{ row.delayState === '延期中' ? '延期' + (row.delayDays || 0) + '天' : '延期完成' }}
            </span>
            <span v-else class="muted">—</span>
          </template>
        </el-table-column>
      </el-table>

      <!-- 交付文件 -->
      <div class="block-title file-block-title">
        <span>交付文件（{{ fileList.length }}）</span>
        <page-button type="write" el-type="primary" size="small" @click="openFileForm">上传文件</page-button>
      </div>
      <el-table :data="deliveryRows" border size="small" :empty-text="'暂无登记文件'">
        <el-table-column label="文件模板" width="140" :show-overflow-tooltip="true">
          <template v-slot="{row}">{{ row.templateName || '—' }}</template>
        </el-table-column>
        <el-table-column label="文件名称" min-width="200">
          <template v-slot="{row}">
            <b v-if="!row.empty" class="file-name-link" @click="handleFileClick(row)">{{ row.fileName || '—' }}</b>
            <span v-if="row.version" class="file-ver">v{{ row.version }}</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="100">
          <template v-slot="{row}">{{ row.empty ? '' : (row.fileType || '—') }}</template>
        </el-table-column>
        <el-table-column label="源类型" width="90" align="center">
          <template v-slot="{row}">
            <el-tag v-if="!row.empty" size="small" :type="originalTypeTag(row.fileOriginalType)">{{ originalTypeLabel(row.fileOriginalType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="路径" min-width="200" :show-overflow-tooltip="true">
          <template v-slot="{row}">{{ row.empty ? '' : (row.filePath || '—') }}</template>
        </el-table-column>
        <el-table-column label="登记人" width="100">
          <template v-slot="{row}">{{ row.empty ? '' : (row.createdName || '—') }}</template>
        </el-table-column>
        <el-table-column label="登记时间" width="170">
          <template v-slot="{row}">{{ row.empty ? '' : dateTime(row.createdTime) }}</template>
        </el-table-column>
        <el-table-column label="修改人" width="100">
          <template v-slot="{row}">{{ row.empty ? '' : (row.updatedName || '—') }}</template>
        </el-table-column>
        <el-table-column label="修改时间" width="170">
          <template v-slot="{row}">{{ row.empty ? '' : dateTime(row.updatedTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template v-slot="{row}">
            <table-button v-if="!row.empty" @click="openFileDetail(row)">详情</table-button>
          </template>
        </el-table-column>
      </el-table>
      <!-- 项目人员 / 测试人员 -->
      <div class="people-cols">
        <div>
          <div class="block-title">项目人员（提出人 / 项目经理 / 任务负责人）</div>
          <div v-if="members.length" class="people-tags">
            <el-tag v-for="m in members" :key="m.employeeId" size="small" effect="plain">{{ m.employeeName || m.employeeId }}</el-tag>
          </div>
          <div v-else class="empty-block">暂无项目人员</div>
        </div>
        <div>
          <div class="block-title">测试人员</div>
          <div v-if="testers.length" class="people-tags">
            <el-tag v-for="t in testers" :key="t.employeeId" size="small" effect="plain" type="warning">{{ t.employeeName || t.employeeId }}</el-tag>
          </div>
          <div v-else class="empty-block">暂无测试人员</div>
        </div>
      </div>
      <!-- 项目日志 -->
      <div class="block-title">项目日志</div>
      <el-timeline v-if="logList.length">
        <el-timeline-item v-for="log in logList" :key="log.id" :timestamp="dateTime(log.createdTime) + ' · ' + (log.createdName || '—')" placement="top">
          <div class="log-line">
            <el-tag size="small" type="info">{{ log.action }}</el-tag>
            <span class="log-detail">{{ log.detail }}</span>
          </div>
        </el-timeline-item>
      </el-timeline>
      <div v-else class="empty-block">暂无动态</div>


    </div>

    <!-- 上传交付文件：所属项目固定为当前项目，不可修改 -->
    <el-dialog v-model="showFileForm" title="上传交付文件" dialog-width="1" :close-on-click-modal="false"
               @opened="clearSourceUpload">
      <form-items :data="fileForm" :columns="1" label-width="2.4rem" icon-width="0" ref="fileFormRef">
        <form-item label="所属项目" :required="true">
          <el-select v-model="fileForm.projectId" disabled style="width: 100%">
            <el-option :label="projectLabel || projectId" :value="projectId"/>
          </el-select>
        </form-item>
        <form-item label="文件模板" :required="true" name="templateId" error-message="请选择文件模板">
          <el-select v-model="fileForm.templateId" placeholder="请选择文件模板" filterable style="width: 100%">
            <el-option v-for="t in fileTemplateOptions" :key="t.id" :label="t.fileName" :value="t.id"/>
          </el-select>
        </form-item>
        <form-item label="文件名称" :required="true" name="fileName" error-message="请填写文件名称">
          <el-input v-model="fileForm.fileName" placeholder="文件名称，如：接口文档.docx"/>
        </form-item>
        <form-item label="文件源类型" :required="true">
          <el-select v-model="fileForm.fileOriginalType" style="width: 100%" @change="onOriginalTypeChange">
            <el-option v-for="t in originalTypeOptions" :key="t.value" :label="t.label" :value="t.value"/>
          </el-select>
        </form-item>
        <form-item v-if="fileForm.fileOriginalType === 1" label="文件路径">
          <el-input v-model="fileForm.filePath" placeholder="URL 地址"/>
        </form-item>
        <form-item v-else label="源文件" :required="sourceFileRequired">
          <el-upload
              ref="sourceUpload"
              :auto-upload="false"
              :limit="1"
              :accept="uploadAccept"
              :on-change="handleSourceFileChange"
              :on-remove="handleSourceFileRemove"
              :on-exceed="handleSourceFileExceed">
            <!-- 不能用 page-button：内部 el-button 带了 @click.stop，会拦截 el-upload 的点击选择 -->
            <el-button>选择文件</el-button>
            <template #tip>
              <div class="upload-tip">{{ uploadTip }}</div>
            </template>
          </el-upload>
        </form-item>
        <form-item label="版本号">
          <el-input-number v-model="fileForm.version" :min="1" :max="9999" controls-position="right" style="width: 100%"/>
        </form-item>
        <form-item label="变更说明" :show-block="true">
          <el-input v-model="fileForm.note" type="textarea" :rows="2" placeholder="变更/说明（可选）"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showFileForm = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveFile">保存</page-button>
      </template>
    </el-dialog>

    <file-record-detail-dialog v-model="showFileDetail" :record="detailRecord" :project-name="projectName"/>
  </div>
</template>

<script>
import {useRoute, useRouter} from "vue-router";
import projectApi from "@/api/dz-project/project/projectApi";
import fileApi from "@/api/dz-project/project/fileApi";
import TableButton from "@/components/h5/button/TableButton.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import FileRecordDetailDialog from "./FileRecordDetailDialog.vue";
import {taskStatusLabel as taskStatusLabelOf, taskStatusTag as taskStatusTagOf} from "./projectStatus";

const ORIGINAL_TYPE_MAP = {
  1: {label: "URL", tag: "info"},
  2: {label: "Word", tag: "primary"},
  3: {label: "Excel", tag: "success"},
  4: {label: "PPT", tag: "warning"},
  5: {label: "Markdown", tag: "info"},
};

const ORIGINAL_TYPE_OPTIONS = [
  {value: 1, label: "URL", accept: ""},
  {value: 2, label: "Word", accept: ".docx"},
  {value: 3, label: "Excel", accept: ".xlsx"},
  {value: 4, label: "PPT", accept: ".ppt,.pptx"},
  {value: 5, label: "Markdown", accept: ".md,.markdown"},
];

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ProjectInfo",
  components: {TableButton, PageButton, FormItems, FormItem, FileRecordDetailDialog},
  props: {
    projectId: {type: String, default: ""},
    projectLabel: {type: String, default: ""},
    projectName: {type: String, default: ""},
  },
  data() {
    return {
      sprintList: [],
      taskList: [],
      fileList: [],
      logList: [],
      members: [],
      testers: [],
      showFileDetail: false,
      detailRecord: {},
      showFileForm: false,
      fileForm: {},
      fileTemplateOptions: [],
      sourceFile: null,
      originalTypeOptions: ORIGINAL_TYPE_OPTIONS,
    };
  },
  computed: {
    /** 交付文件按模板展开：模板没有文件时也保留一行空记录 */
    deliveryRows() {
      const files = this.fileList || [];
      const templates = (this.fileTemplateOptions || []).slice().sort((a, b) => {
        const an = a.templateIndex === null || a.templateIndex === undefined || a.templateIndex === "";
        const bn = b.templateIndex === null || b.templateIndex === undefined || b.templateIndex === "";
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
        return Number(a.templateIndex) - Number(b.templateIndex);
      });
      const rows = [];
      const used = new Set();
      templates.forEach(template => {
        const matched = files.filter(file => file.templateId === template.id);
        if (!matched.length) {
          rows.push({templateId: template.id, templateName: template.fileName, empty: true});
        } else {
          matched.forEach(file => rows.push({...file, templateName: template.fileName, empty: false}));
        }
        used.add(template.id);
      });
      // 找不到模板的历史文件放最后，避免丢数据
      files.filter(file => !used.has(file.templateId)).forEach(file => {
        rows.push({...file, templateName: file.templateName || "", empty: false});
      });
      return rows;
    },
    uploadAccept() {
      const type = Number(this.fileForm.fileOriginalType);
      const option = ORIGINAL_TYPE_OPTIONS.find(o => o.value === type);
      return option ? option.accept : "";
    },
    sourceFileRequired() {
      // Word/Excel 可以不上传源文件，直接创建空白文档去系统内编辑；PPT 必须上传
      return Number(this.fileForm.fileOriginalType) === 4;
    },
    uploadTip() {
      const type = Number(this.fileForm.fileOriginalType);
      if (type === 2) {
        return "可选：不上传则创建空白 Word 文档，保存后进入在线编辑；上传仅支持 .docx";
      }
      if (type === 3) {
        return "可选：不上传则创建空白 Excel，保存后进入在线编辑；上传仅支持 .xlsx";
      }
      if (type === 4) {
        return "必须上传 .ppt / .pptx；PPT 只支持上传和下载";
      }
      if (type === 5) {
        return "可选：不上传则创建空白 Markdown 文档，保存后进入在线编辑；上传支持 .md / .markdown（UTF-8）";
      }
      return "";
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadAll();
      },
    },
  },
  methods: {
    async loadAll() {
      if (!this.projectId) {
        return;
      }
      await Promise.all([
        this.loadSprints(), this.loadTasks(), this.loadFiles(), this.loadLogs(), this.loadPeople(),
        this.loadFileTemplates(),
      ]);
    },
    async loadSprints() {
      const list = await projectApi.queryProjectSprints(this.projectId);
      this.sprintList = list || [];
    },
    async loadTasks() {
      const list = await projectApi.queryProjectTasks(this.projectId);
      this.taskList = list || [];
    },
    async loadFiles() {
      const list = await fileApi.queryProjectFiles(this.projectId);
      this.fileList = list || [];
    },
    async loadFileTemplates() {
      try {
        const list = await fileApi.queryFileTemplateOptions();
        this.fileTemplateOptions = (list || []).slice().sort((a, b) => {
          const an = a.templateIndex === null || a.templateIndex === undefined || a.templateIndex === "";
          const bn = b.templateIndex === null || b.templateIndex === undefined || b.templateIndex === "";
          if (an && bn) return 0;
          if (an) return 1;
          if (bn) return -1;
          return Number(a.templateIndex) - Number(b.templateIndex);
        });
      } catch (e) {
        this.fileTemplateOptions = [];
      }
    },
    async loadLogs() {
      const list = await projectApi.queryProjectLogs(this.projectId);
      this.logList = list || [];
    },
    async loadPeople() {
      const vo = await projectApi.queryProjectPeople(this.projectId);
      this.members = (vo && vo.members) || [];
      this.testers = (vo && vo.testers) || [];
    },
    openFileDetail(row) {
      this.detailRecord = row || {};
      this.showFileDetail = true;
    },
    /** 文件名称：URL 复制地址，其它类型直接进入编辑页 */
    handleFileClick(row) {
      if (!row || row.empty || !row.id) {
        return;
      }
      if (Number(row.fileOriginalType) === 1) {
        this.copyUrl(row);
        return;
      }
      this.router.push({path: "/project/dev/file/edit", query: {id: row.id}});
    },
    async copyUrl(row) {
      const text = (row && row.filePath) || "";
      if (!text) {
        this.$message.warning("文件地址为空");
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        this.$message.success("URL 已复制");
      } catch (e) {
        this.$message.error("复制失败");
      }
    },
    // ============ 上传交付文件（所属项目固定为当前项目） ============
    async openFileForm() {
      this.fileForm = {
        projectId: this.projectId,
        templateId: "",
        fileName: "",
        filePath: "",
        // 项目详情入口以「上传源文件」为主，默认 Word；可切换 Excel/PPT/URL
        fileOriginalType: 2,
        version: 1,
        note: "",
      };
      this.sourceFile = null;
      this.showFileForm = true;
      this.$nextTick(() => this.clearSourceUpload());
      await this.loadFileTemplates();
    },
    clearSourceUpload() {
      if (this.$refs.sourceUpload && this.$refs.sourceUpload.clearFiles) {
        this.$refs.sourceUpload.clearFiles();
      }
    },
    onOriginalTypeChange() {
      this.sourceFile = null;
      this.clearSourceUpload();
    },
    handleSourceFileChange(file) {
      this.sourceFile = file && file.raw ? file.raw : null;
      if (this.sourceFile && !this.fileForm.fileName) {
        this.fileForm.fileName = this.sourceFile.name;
      }
    },
    handleSourceFileRemove() {
      this.sourceFile = null;
    },
    handleSourceFileExceed() {
      this.$message.warning("一次只能上传一个文件，请先移除已选文件");
    },
    async saveFile() {
      if (!this.$refs.fileFormRef.validCheck()) {
        return;
      }
      // 项目固定为当前项目，前端即使用户改了也不作数，后端仍会校验
      this.fileForm.projectId = this.projectId;
      const type = Number(this.fileForm.fileOriginalType) || 1;
      let newId = null;
      if (type === 1) {
        if (!this.fileForm.filePath) {
          this.$message.warning("请填写文件路径");
          return;
        }
        const payload = JSON.parse(JSON.stringify(this.fileForm));
        payload.fileName = payload.fileName || null;
        payload.filePath = payload.filePath || null;
        payload.note = payload.note || null;
        await fileApi.save(payload);
      } else {
        if (type === 4 && !this.sourceFile) {
          this.$message.warning("PPT 请上传源文件");
          return;
        }
        const formData = new FormData();
        const fields = ["projectId", "templateId", "fileName", "fileType", "fileOriginalType", "version", "note"];
        fields.forEach(key => {
          const value = this.fileForm[key];
          if (value !== null && value !== undefined && value !== "") {
            formData.append(key, value);
          }
        });
        if (this.sourceFile) {
          formData.append("file", this.sourceFile);
        }
        newId = await fileApi.saveWithFile(formData);
      }
      this.showFileForm = false;
      this.loadFiles();
      // 未上传源文件的 Word/Excel/Markdown：登记后直接进入在线编辑创建内容
      if (newId && !this.sourceFile && (type === 2 || type === 3 || type === 5)) {
        this.router.push({path: "/project/dev/file/edit", query: {id: newId}});
      }
    },
    originalTypeLabel(type) {
      return (ORIGINAL_TYPE_MAP[Number(type) || 1] || {}).label || "—";
    },
    originalTypeTag(type) {
      return (ORIGINAL_TYPE_MAP[Number(type) || 1] || {}).tag || "info";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    dateTime(v) {
      return v ? String(v).slice(0, 16) : "—";
    },
    sprintStateTag(state) {
      return {排期中: "warning", 未开始: "info", 进行中: "success", 已结束: "info", 已到期: "danger"}[state] || "info";
    },
    taskStatusLabel(s) {
      return taskStatusLabelOf(s);
    },
    taskStatusTag(s) {
      return taskStatusTagOf(s);
    },
  },
};
</script>

<style lang="scss" scoped>
.project-info {
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  .info-body {
    padding: 2px;
  }
  .block-title {
    font-weight: 700;
    margin: 14px 0 8px;
    color: var(--yq-font-color-base, #303133);
  }
  .file-block-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .upload-tip {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.6;
    color: #909399;
  }
  .people-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 20px;
    margin-top: 14px;
  }
  .code-text {
    font-family: Consolas, Menlo, monospace;
    font-size: 11px;
  }
  .muted {
    color: var(--yq-font-color-desc);
  }
  .file-name-link {
    color: #1677ff;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .progress-text {
    margin-left: 6px;
    font-size: 12px;
    color: var(--yq-font-color-desc);
  }
  .file-ver {
    margin-left: 6px;
    font-size: 11px;
    color: #909399;
    background: #f0f2f5;
    border-radius: 3px;
    padding: 0 4px;
  }
  .delay-text {
    color: #f56c6c;
    font-weight: 600;
  }
  .empty-block {
    color: #909399;
    padding: 12px 0;
  }
  .people-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .log-line {
    display: flex;
    align-items: center;
    gap: 8px;
    .log-detail {
      color: var(--yq-font-color-desc);
    }
  }
  .sprint-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
    .sprint-card {
      border: 1px solid var(--el-border-color-lighter);
      border-radius: 6px;
      padding: 10px 12px;
      background: #fff;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
      .sc-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .sc-code {
          font-family: Consolas, Menlo, monospace;
          color: var(--yq-font-color-desc);
          font-size: 12px;
        }
      }
      .sc-name {
        font-weight: 600;
        margin: 6px 0 2px;
      }
      .sc-goal {
        color: var(--yq-font-color-desc);
        font-size: 12px;
        margin-bottom: 6px;
      }
      .sc-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0 10px;
        font-size: 12px;
        color: #909399;
      }
      .sc-progress {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        .el-progress {
          flex: 1;
        }
        .progress-text {
          font-size: 12px;
          color: var(--yq-font-color-desc);
        }
      }
    }
  }
}
</style>
